import React, { useState, useEffect } from 'react';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import financialConfigService from '../../services/financialConfigService';
import { getQuickBooksEntities } from '../../services/quickbooksService';

export default function StoreMappingManager() {
  const { confirmDialogProps, confirm } = useConfirmDialog();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [qbEntities, setQbEntities] = useState([]);
  const [stores, setStores] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [editingSuggestion, setEditingSuggestion] = useState(null); // Track if editing a suggestion
  const [formData, setFormData] = useState({
    store_id: '',
    qb_connector_id: '',
    mapping_method: 'manual',
    effective_from: '',
    effective_to: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load data independently with individual error handling
      try {
        const mappingsRes = await financialConfigService.getStoreMappings();
        if (mappingsRes?.success && mappingsRes?.data) {
          setMappings(mappingsRes.data);
        }
      } catch (err) {
        console.error('Error loading store mappings:', err);
        setMappings([]);
      }

      try {
        const entitiesRes = await getQuickBooksEntities();
        console.log('QB Entities API response:', entitiesRes);
        if (entitiesRes?.success && entitiesRes?.data) {
          console.log('QB Entities data:', entitiesRes.data);
          setQbEntities(entitiesRes.data);
        } else {
          console.warn('QB entities response invalid:', entitiesRes);
          setQbEntities([]);
        }
      } catch (err) {
        console.error('Error loading QB entities:', err);
        setQbEntities([]);
      }

      try {
        const storesRes = await financialConfigService.getStores();
        console.log('Stores API response:', storesRes);
        if (storesRes?.success && storesRes?.data) {
          const mappedStores = storesRes.data.map(s => ({
            id: s.store_id,
            name: `${s.brand_name} - ${s.store_name} (${s.market})`,
            brand_name: s.brand_name,
            brand_id: s.brand_id
          }));
          console.log('Mapped stores:', mappedStores);
          setStores(mappedStores);
        } else {
          console.warn('Stores response invalid:', storesRes);
          setStores([]);
        }
      } catch (err) {
        console.error('Error loading stores:', err);
        setStores([]);
      }

      try {
        const suggestionsRes = await financialConfigService.getAutoSuggestions();
        console.log('Auto suggestions API response:', suggestionsRes);
        if (suggestionsRes?.success && suggestionsRes?.data) {
          // Backend now returns { suggestions: [], unmapped: [] }
          setSuggestions(suggestionsRes.data.suggestions || []);
          setUnmapped(suggestionsRes.data.unmapped || []);
        }
      } catch (err) {
        console.error('Error loading auto suggestions:', err);
        setSuggestions([]);
        setUnmapped([]);
      }

    } catch (err) {
      console.error('Error loading store mappings data:', err);
      setError('Failed to load store mappings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    // Handle Date objects or ISO strings
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    // Return YYYY-MM-DD format for input[type="date"]
    return date.toISOString().split('T')[0];
  };

  const handleOpenDialog = (mapping = null) => {
    if (mapping) {
      setEditingMapping(mapping);
      const formState = {
        store_id: mapping.store_id,
        qb_connector_id: mapping.qb_connector_id,
        mapping_method: mapping.mapping_method || 'manual',
        effective_from: formatDateForInput(mapping.effective_from),
        effective_to: formatDateForInput(mapping.effective_to),
        notes: mapping.notes || ''
      };
      setFormData(formState);
    } else {
      setEditingMapping(null);
      setFormData({
        store_id: '',
        qb_connector_id: '',
        mapping_method: 'manual',
        effective_from: '',
        effective_to: '',
        notes: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingMapping(null);
    setEditingSuggestion(null);
  };

  const handleSuggestMapping = async (storeId) => {
    try {
      const selectedStore = stores.find(s => s.id === storeId);
      if (!selectedStore || !selectedStore.brand_id) {
        alert('Please select a store first');
        return;
      }

      const result = await financialConfigService.suggestQBEntity(storeId, selectedStore.brand_id);
      if (result.success && result.data.suggested_qb_connector_id) {
        setFormData(prev => ({
          ...prev,
          qb_connector_id: result.data.suggested_qb_connector_id,
          mapping_method: 'brand_inferred'
        }));
        alert(`Suggested QB Entity based on brand rules (${result.data.confidence_level}% confidence): ${result.data.reason}`);
      } else {
        alert('No suggestion available. Please select manually.');
      }
    } catch (err) {
      console.error('Error getting suggestion:', err);
      alert('Failed to get suggestion');
    }
  };

  const handleSubmit = async () => {
    try {
      let brandId;

      if (editingMapping) {
        // When editing existing mapping, use the brand_id from the existing mapping
        brandId = editingMapping.brand_id;
      } else if (editingSuggestion) {
        // When editing a suggestion, use the brand_id from the suggestion
        brandId = editingSuggestion.brand_id;
      } else {
        // When creating new mapping, look up the store to get brand_id
        const selectedStore = stores.find(s => s.id === formData.store_id);
        if (!selectedStore || !selectedStore.brand_id) {
          alert('Please select a store');
          return;
        }
        brandId = selectedStore.brand_id;
      }

      if (!formData.qb_connector_id) {
        alert('Please select a QuickBooks Entity');
        return;
      }

      const payload = {
        ...formData,
        brand_id: brandId
      };

      if (editingMapping) {
        await financialConfigService.updateStoreMapping(editingMapping.id, payload);
      } else {
        await financialConfigService.createStoreMapping(payload);
      }

      loadData();
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving mapping:', err);
      alert('Failed to save mapping');
    }
  };

  const handleDelete = async (id) => {
    if (!await confirm({ title: 'Delete Mapping', message: 'Are you sure you want to delete this mapping?', confirmText: 'Delete', severity: 'danger' })) {
      return;
    }

    try {
      await financialConfigService.deleteStoreMapping(id);
      loadData();
    } catch (err) {
      console.error('Error deleting mapping:', err);
      alert('Failed to delete mapping');
    }
  };

  const handleApproveAll = async () => {
    if (suggestions.length === 0) {
      return;
    }

    if (!await confirm({ title: 'Approve All Mappings', message: `Are you sure you want to approve all ${suggestions.length} suggested mappings?`, confirmText: 'Approve All', severity: 'info' })) {
      return;
    }

    setLoadingSuggestions(true);
    let successCount = 0;
    let errorCount = 0;

    for (const suggestion of suggestions) {
      try {
        await financialConfigService.createStoreMapping({
          store_id: suggestion.store_id,
          brand_id: suggestion.brand_id,
          qb_connector_id: suggestion.suggested_qb_connector_id,
          mapping_method: suggestion.mapping_method,
          notes: `Auto-suggested: ${suggestion.reason}`
        });
        successCount++;
      } catch (err) {
        console.error(`Error approving mapping for store ${suggestion.store_id}:`, err);
        errorCount++;
      }
    }

    setLoadingSuggestions(false);
    alert(`Approved ${successCount} mappings successfully. ${errorCount > 0 ? `${errorCount} failed.` : ''}`);
    loadData();
  };

  const handleEditSuggestion = (suggestion) => {
    // Open dialog with suggestion pre-filled, allowing user to modify
    setEditingMapping(null); // Not editing existing mapping
    setEditingSuggestion(suggestion); // Store the suggestion being edited
    setFormData({
      store_id: suggestion.store_id,
      qb_connector_id: suggestion.suggested_qb_connector_id,
      mapping_method: 'manual', // Changed from auto-suggested to manual
      effective_from: '',
      effective_to: '',
      notes: `Modified from suggestion: ${suggestion.reason}`
    });
    setDialogOpen(true);
  };

  const handleRejectSuggestion = async (suggestion) => {
    // Remove suggestion from list by reloading data
    // In practice, you could add a "dismissed suggestions" feature
    if (!await confirm({ title: 'Reject Suggestion', message: `Reject suggestion for ${suggestion.store_name}? You can manually map this store later.`, confirmText: 'Reject', severity: 'warning' })) {
      return;
    }
    // For now, just remove from UI by filtering
    setSuggestions(prev => prev.filter(s => s.store_id !== suggestion.store_id));
  };

  const handleMapUnmappedStore = (unmappedStore) => {
    // Open dialog to manually map this store
    setEditingMapping(null); // Not editing existing mapping
    setEditingSuggestion(null); // Not editing a suggestion
    setFormData({
      store_id: unmappedStore.store_id,
      qb_connector_id: '',
      mapping_method: 'manual',
      effective_from: '',
      effective_to: '',
      notes: `Manual mapping for ${unmappedStore.store_name} - ${unmappedStore.reason}`
    });
    setDialogOpen(true);
  };

  const handleApproveSingle = async (suggestion) => {
    try {
      await financialConfigService.createStoreMapping({
        store_id: suggestion.store_id,
        brand_id: suggestion.brand_id,
        qb_connector_id: suggestion.suggested_qb_connector_id,
        mapping_method: suggestion.mapping_method,
        notes: `Auto-suggested: ${suggestion.reason}`
      });
      loadData();
    } catch (err) {
      console.error('Error approving suggestion:', err);
      alert('Failed to approve suggestion');
    }
  };

  const getMappingMethodColor = (method) => {
    const colors = {
      manual: 'default',
      currency_inferred: 'info',
      brand_inferred: 'success',
      geography_inferred: 'warning'
    };
    return colors[method] || 'default';
  };

  const getMappingMethodLabel = (method) => {
    const labels = {
      manual: 'Manual',
      currency_inferred: 'Currency',
      brand_inferred: 'Brand Rule',
      geography_inferred: 'Geography'
    };
    return labels[method] || method;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight={600}>
            Store-to-QuickBooks Mapping
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Map each store to its corresponding QuickBooks entity for revenue reconciliation
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Mapping
        </Button>
      </Box>

      {/* Auto-Suggestions Section */}
      {suggestions.length > 0 && (
        <Card sx={{ mb: 3, bgcolor: '#f5f9ff' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Suggested Mappings ({suggestions.length})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Auto-generated suggestions for unmapped stores based on brand rules
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="success"
                onClick={handleApproveAll}
                disabled={loadingSuggestions}
                startIcon={<CheckCircleIcon />}
              >
                {loadingSuggestions ? 'Approving...' : 'Approve All'}
              </Button>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Store</TableCell>
                    <TableCell>Brand</TableCell>
                    <TableCell>Suggested QB Entity</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Confidence</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {suggestions.map((suggestion) => (
                    <TableRow key={suggestion.store_id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {suggestion.store_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {suggestion.market} • {suggestion.currency}
                        </Typography>
                      </TableCell>
                      <TableCell>{suggestion.brand_name}</TableCell>
                      <TableCell>{suggestion.suggested_qb_entity_name}</TableCell>
                      <TableCell>
                        <Chip
                          label={getMappingMethodLabel(suggestion.mapping_method)}
                          size="small"
                          color={getMappingMethodColor(suggestion.mapping_method)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {suggestion.confidence_level}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {suggestion.reason}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Tooltip title="Edit before approving">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditSuggestion(suggestion)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject suggestion">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRejectSuggestion(suggestion)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            onClick={() => handleApproveSingle(suggestion)}
                            startIcon={<CheckCircleIcon />}
                          >
                            Approve
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Unmapped Stores Section (no suggestions, need manual mapping) */}
      {unmapped.length > 0 && (
        <Card sx={{ mb: 3, bgcolor: '#fff9f0', borderColor: 'warning.main', borderWidth: 1, borderStyle: 'solid' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" fontWeight={600} color="warning.dark">
                  Unmapped Stores ({unmapped.length})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  These stores have no matching mapping rules and require manual mapping
                </Typography>
              </Box>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Store</TableCell>
                    <TableCell>Brand</TableCell>
                    <TableCell>Market / Currency</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unmapped.map((store) => (
                    <TableRow key={store.store_id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {store.store_name}
                        </Typography>
                      </TableCell>
                      <TableCell>{store.brand_name}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {store.market} • {store.currency}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {store.reason}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          onClick={() => handleMapUnmappedStore(store)}
                          startIcon={<AddIcon />}
                        >
                          Map Manually
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Mappings Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Store ID</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>QuickBooks Entity</TableCell>
                  <TableCell>Mapping Method</TableCell>
                  <TableCell>Confidence</TableCell>
                  <TableCell>Effective Dates</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mappings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No store mappings configured yet. Click "Add Mapping" to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  mappings.map((mapping) => (
                    <TableRow key={mapping.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {mapping.store_name || mapping.store_id}
                        </Typography>
                      </TableCell>
                      <TableCell>{mapping.brand_name}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {mapping.is_active ? (
                            <CheckCircleIcon fontSize="small" color="success" />
                          ) : null}
                          {mapping.qb_entity_name}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getMappingMethodLabel(mapping.mapping_method)}
                          size="small"
                          color={getMappingMethodColor(mapping.mapping_method)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {mapping.confidence_level}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontSize="0.875rem">
                          {new Date(mapping.effective_from).toLocaleDateString()}
                        </Typography>
                        {mapping.effective_to && (
                          <Typography variant="body2" fontSize="0.75rem" color="text.secondary">
                            to {new Date(mapping.effective_to).toLocaleDateString()}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {mapping.notes || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(mapping)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(mapping.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { maxHeight: '90vh' } }}
      >
        <DialogTitle>
          {editingMapping ? 'Edit Store Mapping' : editingSuggestion ? 'Modify Suggested Mapping' : 'Add Store Mapping'}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
            {editingMapping || editingSuggestion ? (
              <TextField
                fullWidth
                label="Store"
                value={
                  editingMapping
                    ? (editingMapping.store_name || editingMapping.store_id)
                    : (editingSuggestion.store_name || editingSuggestion.store_id)
                }
                disabled
                helperText="Store cannot be changed when editing"
              />
            ) : (
              <FormControl fullWidth>
                <InputLabel>Store</InputLabel>
                <Select
                  value={formData.store_id}
                  label="Store"
                  onChange={(e) => setFormData({ ...formData, store_id: e.target.value })}
                >
                  {stores.map((store) => (
                    <MenuItem key={store.id} value={store.id}>
                      {store.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControl fullWidth>
                <InputLabel>QuickBooks Entity</InputLabel>
                <Select
                  value={formData.qb_connector_id}
                  label="QuickBooks Entity"
                  onChange={(e) => setFormData({ ...formData, qb_connector_id: e.target.value })}
                >
                  {qbEntities.map((entity) => (
                    <MenuItem key={entity.connector_id} value={entity.connector_id}>
                      {entity.company_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {formData.store_id && (
                <Tooltip title="Get AI Suggestion">
                  <IconButton
                    color="primary"
                    onClick={() => handleSuggestMapping(formData.store_id)}
                  >
                    <AutoFixHighIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <FormControl fullWidth>
              <InputLabel>Mapping Method</InputLabel>
              <Select
                value={formData.mapping_method}
                label="Mapping Method"
                onChange={(e) => setFormData({ ...formData, mapping_method: e.target.value })}
              >
                {financialConfigService.MAPPING_METHODS.map((method) => (
                  <MenuItem key={method.value} value={method.value}>
                    {method.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                type="date"
                label="Effective From"
                value={formData.effective_from}
                onChange={(e) => setFormData({ ...formData, effective_from: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="When did this store start operating under this QB company?"
              />
              <TextField
                fullWidth
                type="date"
                label="Effective To (Optional)"
                value={formData.effective_to}
                onChange={(e) => setFormData({ ...formData, effective_to: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Leave empty if still active"
              />
            </Box>

            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Optional notes about this mapping..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.store_id || !formData.qb_connector_id}
          >
            {editingMapping ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog {...confirmDialogProps} />
    </Box>
  );
}
